/**
 * Content Analytics Service
 * Fetches analytics data from Firebase Functions API
 */

import { database } from '../config/firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

export interface TopBookRow {
  book_id: string;
  book_title: string | null;
  book_author: string | null;
  book_category: string | null;
  event_count?: number;
  // Download metrics
  download_count?: number;
  unique_downloaders?: number;
  last_download_timestamp?: number | null;
  first_download_timestamp?: number | null;
  // Enriched data
  coverUrl?: string;
  description?: string;
  isPublished?: boolean;
  pageCount?: number;
  language?: string;
  isAvailable?: boolean; // true if document exists in Firestore, false if deleted
}

export interface TopAudioRow {
  audio_id: string;
  audio_title: string | null;
  audio_author: string | null;
  event_count?: number;
  // Download metrics
  download_count?: number;
  unique_downloaders?: number;
  last_download_timestamp?: number | null;
  first_download_timestamp?: number | null;
  // Enriched data
  coverUrl?: string;
  description?: string;
  isPublished?: boolean;
  duration?: number;
  language?: string;
  isAvailable?: boolean; // true if document exists in Firestore, false if deleted
}

export interface TopVideoRow {
  video_id: string;
  video_title: string | null;
  video_category: string | null;
  event_count?: number;
  // Download metrics
  download_count?: number;
  unique_downloaders?: number;
  last_download_timestamp?: number | null;
  first_download_timestamp?: number | null;
  // Enriched data
  coverUrl?: string; // use video thumbnail for display parity
  thumbnailUrl?: string;
  description?: string;
  isPublished?: boolean;
  isAvailable?: boolean; // true if document exists in Firestore, false if deleted
}

export interface TopUserRow {
  user_id: string;
  engagement_seconds: number;
  sessions: number;
  book_views: number;
  book_completed: number;
  audio_views: number;
  audio_completed: number;
  last_active_datetime_ist: string;
  // Enriched data
  user_name?: string;
  center?: string;
  email?: string;
}

export interface AnalyticsResponse<T> {
  data: T[];
  limit: number;
  days: number;
}

class ContentAnalyticsService {
  /**
   * Fetch top viewed books
   */
  async getTopViewedBooks(days: number = 30): Promise<TopBookRow[]> {
    try {
      const response = await fetch(`${BASE_URL}/analyticsTopViewedBooks?days=${days}&limit=100`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result: AnalyticsResponse<TopBookRow> = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error fetching top viewed books:', error);
      throw error;
    }
  }

  /**
   * Fetch top reading started books
   */
  async getTopReadingStartedBooks(days: number = 30): Promise<TopBookRow[]> {
    try {
      const response = await fetch(`${BASE_URL}/analyticsTopReadingStartedBooks?days=${days}&limit=100`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result: AnalyticsResponse<TopBookRow> = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error fetching top reading started books:', error);
      throw error;
    }
  }

  /**
   * Fetch top completed books
   */
  async getTopCompletedBooks(days: number = 30): Promise<TopBookRow[]> {
    try {
      const response = await fetch(`${BASE_URL}/analyticsTopCompletedBooks?days=${days}&limit=100`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result: AnalyticsResponse<TopBookRow> = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error fetching top completed books:', error);
      throw error;
    }
  }

  /**
   * Fetch top shared books
   */
  async getTopSharedBooks(days: number = 30): Promise<TopBookRow[]> {
    try {
      const response = await fetch(`${BASE_URL}/analyticsTopSharedBooks?days=${days}&limit=100`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result: AnalyticsResponse<TopBookRow> = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error fetching top shared books:', error);
      throw error;
    }
  }

  /**
   * Fetch top viewed audios
   */
  async getTopViewedAudios(days: number = 30): Promise<TopAudioRow[]> {
    try {
      const response = await fetch(`${BASE_URL}/analyticsTopViewedAudios?days=${days}&limit=100`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result: AnalyticsResponse<TopAudioRow> = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error fetching top viewed audios:', error);
      throw error;
    }
  }

  /**
   * Fetch top completed audios
   */
  async getTopCompletedAudios(days: number = 30): Promise<TopAudioRow[]> {
    try {
      const response = await fetch(`${BASE_URL}/analyticsTopCompletedAudios?days=${days}&limit=100`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result: AnalyticsResponse<TopAudioRow> = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error fetching top completed audios:', error);
      throw error;
    }
  }

  /**
   * Fetch top shared audios
   */
  async getTopSharedAudios(days: number = 30): Promise<TopAudioRow[]> {
    try {
      const response = await fetch(`${BASE_URL}/analyticsTopSharedAudios?days=${days}&limit=100`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result: AnalyticsResponse<TopAudioRow> = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error fetching top shared audios:', error);
      throw error;
    }
  }

  /**
   * Fetch top downloaded books
   */
  async getTopDownloadedBooks(days: number = 30): Promise<TopBookRow[]> {
    try {
      const response = await fetch(`${BASE_URL}/analyticsTopDownloadedBooks?days=${days}&limit=100`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result: AnalyticsResponse<TopBookRow> = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error fetching top downloaded books:', error);
      throw error;
    }
  }

  /**
   * Fetch top downloaded audios
   */
  async getTopDownloadedAudios(days: number = 30): Promise<TopAudioRow[]> {
    try {
      const response = await fetch(`${BASE_URL}/analyticsTopDownloadedAudios?days=${days}&limit=100`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result: AnalyticsResponse<TopAudioRow> = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error fetching top downloaded audios:', error);
      throw error;
    }
  }

  /**
   * Fetch top users engagement
   */
  async getTopUsersEngagement(days: number = 30): Promise<TopUserRow[]> {
    try {
      const response = await fetch(`${BASE_URL}/analyticsTopUsersEngagement?days=${days}&limit=100`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result: AnalyticsResponse<TopUserRow> = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error fetching top users engagement:', error);
      throw error;
    }
  }

  /**
   * Fetch top viewed videos
   */
  async getTopViewedVideos(days: number = 30): Promise<TopVideoRow[]> {
    const response = await fetch(`${BASE_URL}/analyticsTopViewedVideos?days=${days}&limit=100`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const result: AnalyticsResponse<TopVideoRow> = await response.json();
    return result.data;
  }

  /**
   * Fetch top completed videos
   */
  async getTopCompletedVideos(days: number = 30): Promise<TopVideoRow[]> {
    const response = await fetch(`${BASE_URL}/analyticsTopCompletedVideos?days=${days}&limit=100`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const result: AnalyticsResponse<TopVideoRow> = await response.json();
    return result.data;
  }

  /**
   * Fetch top shared videos
   */
  async getTopSharedVideos(days: number = 30): Promise<TopVideoRow[]> {
    const response = await fetch(`${BASE_URL}/analyticsTopSharedVideos?days=${days}&limit=100`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const result: AnalyticsResponse<TopVideoRow> = await response.json();
    return result.data;
  }


  /**
   * Fetch all analytics data
   */
  async getAllAnalytics(days: number = 30) {
    try {
      const [
        topViewedBooks,
        topReadingStartedBooks,
        topCompletedBooks,
        topSharedBooks,
        topViewedAudios,
        topCompletedAudios,
        topSharedAudios,
        topDownloadedBooks,
        topDownloadedAudios,
        topUsersEngagement,
      ] = await Promise.all([
        this.getTopViewedBooks(days),
        this.getTopReadingStartedBooks(days),
        this.getTopCompletedBooks(days),
        this.getTopSharedBooks(days),
        this.getTopViewedAudios(days),
        this.getTopCompletedAudios(days),
        this.getTopSharedAudios(days),
          this.getTopDownloadedBooks(days),
          this.getTopDownloadedAudios(days),
        this.getTopUsersEngagement(days),
      ]);

      return {
        topViewedBooks,
        topReadingStartedBooks,
        topCompletedBooks,
        topSharedBooks,
        topViewedAudios,
        topCompletedAudios,
        topSharedAudios,
          topDownloadedBooks,
          topDownloadedAudios,
        topUsersEngagement,
      };
    } catch (error) {
      console.error('Error fetching all analytics:', error);
      throw error;
    }
  }

  /**
   * Enrich book data with details from Firestore
   */
  async enrichBookData(books: TopBookRow[]): Promise<TopBookRow[]> {
    try {
      const enrichedBooks = await Promise.all(
        books.map(async (book) => {
          try {
            const bookDoc = await getDoc(doc(database, 'ebooks', book.book_id));
            if (bookDoc.exists()) {
              const bookData = bookDoc.data();
              
              // Extract cover URL from various possible field structures
              let coverUrl = '';
              if (bookData.coverImage?.url) {
                coverUrl = bookData.coverImage.url;
              } else if (bookData.coverImageUrl) {
                coverUrl = bookData.coverImageUrl;
              } else if (bookData.coverUrl) {
                coverUrl = bookData.coverUrl;
              } else if (typeof bookData.coverImage === 'string') {
                coverUrl = bookData.coverImage;
              }
              
              // Extract page count from various possible locations
              let pageCount = 0;
              if (bookData.pageCount) {
                pageCount = bookData.pageCount;
              } else if (bookData.metadata?.publishing?.pageCount) {
                pageCount = bookData.metadata.publishing.pageCount;
              } else if (bookData.file?.pages) {
                pageCount = bookData.file.pages;
              }
              
              // Extract category name from various structures
              let categoryName = '';
              if (bookData.category?.name) {
                categoryName = bookData.category.name;
              } else if (typeof bookData.category === 'string') {
                categoryName = bookData.category;
              }
              
              const enriched = {
                ...book,
                coverUrl: coverUrl,
                description: bookData.description || '',
                isPublished: bookData.isPublished || false,
                pageCount: pageCount,
                language: bookData.language || '',
                isAvailable: true, // Document exists
                // Use Firestore data if analytics data is missing
                book_title: book.book_title || bookData.title || '',
                book_author: book.book_author || bookData.author || '',
                book_category: book.book_category || categoryName || '',
              };
              
              // Debug logging for missing cover images
              if (!coverUrl && book.book_id) {
                console.warn(`Book ${book.book_id} has no cover image. Available fields:`, Object.keys(bookData));
              }
              
              return enriched;
            } else {
              console.warn(`Book document ${book.book_id} not found in Firestore`);
              // Document doesn't exist - mark as deleted
              return {
                ...book,
                isAvailable: false,
              };
            }
          } catch (error) {
            console.error(`Error fetching book ${book.book_id}:`, error);
            return book;
          }
        })
      );
      return enrichedBooks;
    } catch (error) {
      console.error('Error enriching book data:', error);
      return books;
    }
  }

  /**
   * Enrich audio data with details from Firestore
   */
  async enrichAudioData(audios: TopAudioRow[]): Promise<TopAudioRow[]> {
    try {
      const enrichedAudios = await Promise.all(
        audios.map(async (audio) => {
          try {
            const audioDoc = await getDoc(doc(database, 'audiobooks', audio.audio_id));
            if (audioDoc.exists()) {
              const audioData = audioDoc.data();
              return {
                ...audio,
                coverUrl: audioData.coverImageUrl || audioData.coverUrl || '',
                description: audioData.description || '',
                isPublished: audioData.isPublished || false,
                duration: audioData.duration || 0,
                language: audioData.language || '',
                isAvailable: true, // Document exists
                // Use Firestore data if analytics data is missing
                audio_title: audio.audio_title || audioData.title || '',
                audio_author: audio.audio_author || audioData.author || '',
              };
            }
            // Document doesn't exist - mark as deleted
            return {
              ...audio,
              isAvailable: false,
            };
          } catch (error) {
            console.error(`Error fetching audio ${audio.audio_id}:`, error);
            return audio;
          }
        })
      );
      return enrichedAudios;
    } catch (error) {
      console.error('Error enriching audio data:', error);
      return audios;
    }
  }

  /**
   * Enrich video data with details from Firestore
   */
  async enrichVideoData(videos: TopVideoRow[]): Promise<TopVideoRow[]> {
    try {
      const enrichedVideos = await Promise.all(
        videos.map(async (video) => {
          try {
            const videoDoc = await getDoc(doc(database, 'videos', video.video_id));
            if (videoDoc.exists()) {
              const videoData = videoDoc.data() as any;

              const thumb =
                videoData.thumbnailUrl ||
                videoData.coverUrl ||
                videoData.thumbnail ||
                '';

              const category =
                (typeof videoData.category === 'string' ? videoData.category : videoData.category?.name) || '';

              return {
                ...video,
                thumbnailUrl: thumb,
                coverUrl: thumb,
                description: videoData.description || '',
                isPublished: videoData.isPublished || false,
                isAvailable: true,
                video_title: video.video_title || videoData.title || '',
                video_category: video.video_category || category || '',
              };
            }
            return { ...video, isAvailable: false };
          } catch (error) {
            console.error(`Error fetching video ${video.video_id}:`, error);
            return video;
          }
        })
      );
      return enrichedVideos;
    } catch (error) {
      console.error('Error enriching video data:', error);
      return videos;
    }
  }

  /**
   * Enrich user engagement data with user details from Firestore
   * Looks up users by phone number (user_id)
   */
  async enrichUserData(users: TopUserRow[]): Promise<TopUserRow[]> {
    try {
      const enrichedUsers = await Promise.all(
        users.map(async (user) => {
          try {
            // user_id is phone number like "+917744012232"
            const phoneNumber = user.user_id;
            
            // Try to find user by phone number
            // Check both mobile and phoneNumber fields
            const usersRef = collection(database, 'users');
            const q1 = query(usersRef, where('mobile', '==', phoneNumber));
            const q2 = query(usersRef, where('phoneNumber', '==', phoneNumber));
            
            const [snapshot1, snapshot2] = await Promise.all([
              getDocs(q1),
              getDocs(q2)
            ]);

            // Find the first matching user
            let userDoc = null;
            if (!snapshot1.empty) {
              userDoc = snapshot1.docs[0];
            } else if (!snapshot2.empty) {
              userDoc = snapshot2.docs[0];
            }

            if (userDoc) {
              const userData = userDoc.data();
              return {
                ...user,
                user_name: userData.name || userData.displayName || '',
                center: userData.centerName || userData.center || '',
                email: userData.email || '',
              };
            }
            return user;
          } catch (error) {
            console.error(`Error fetching user ${user.user_id}:`, error);
            return user;
          }
        })
      );
      return enrichedUsers;
    } catch (error) {
      console.error('Error enriching user data:', error);
      return users;
    }
  }
}

export const contentAnalyticsService = new ContentAnalyticsService();

